import { useEffect, useMemo, useState } from "react";
import { useTableFunctions } from "./useTableFunctions";
import { useTeamInfo } from "../../../hooks/useTeams";
import { getErrorMessage } from "../../../services/utils/apiError";
import { IBaseFormProps } from "../types/types";
import { MatchStatusFilter, normalizeMatchStatus } from "../matchStatusDisplay";
import { IGameRecord } from "../../../types/types";
import {
  getMatchSupplementGaps,
  matchHasSupplementGaps,
  MatchSupplementGaps,
} from "../../../utils/gameSupplementHelpers";
import { CompactTableColumn } from "../compactTableColumn";

export interface UseTableListParams<T> {
  data: T[];
  keys: Array<keyof T>;
  idKey: keyof T;
  FormComponent?: React.ComponentType<IBaseFormProps<T>>;
  gameIdsByMatchId?: Record<string, number[]>;
  gamesByMatchId?: Record<string, IGameRecord[]>;
  compactColumns?: CompactTableColumn<T>[];
  ensureGamesLoaded?: () => void;
  onEdit?: (updatedItem: T) => void | Promise<void>;
  onCreateSubmit?: (newItem: Partial<T>) => void | Promise<void>;
  onDeleteConfirm?: (ids: Array<string>) => void | Promise<void>;
}

const coerceValueType = (
  keyStr: string,
  rawValue: string,
  sampleRecord?: object,
): string | number | boolean => {
  if (sampleRecord && keyStr in sampleRecord) {
    const targetType = typeof (sampleRecord as Record<string, unknown>)[keyStr];
    if (targetType === "number") {
      const parsed = Number(rawValue);
      return isNaN(parsed) || rawValue.trim() === "" ? 0 : parsed;
    }
    if (targetType === "boolean") {
      return rawValue === "true" || rawValue === "1";
    }
  }

  const normalizedKey = keyStr.toLowerCase();
  if (
    normalizedKey.endsWith("id") ||
    ["age", "points", "score", "wins", "losses", "kills", "deaths", "assists", "games"].includes(
      normalizedKey,
    )
  ) {
    const parsed = Number(rawValue);
    return isNaN(parsed) || rawValue.trim() === "" ? 0 : parsed;
  }

  if (["isactive", "enabled", "status"].includes(normalizedKey)) {
    return rawValue === "true";
  }

  return rawValue;
};

export const useTableList = <T extends object>({
  data,
  keys,
  idKey,
  FormComponent,
  gameIdsByMatchId,
  gamesByMatchId,
  compactColumns,
  ensureGamesLoaded,
  onEdit,
  onCreateSubmit,
  onDeleteConfirm,
}: UseTableListParams<T>) => {
  const rawTeams = useTeamInfo();
  const availableTeams = Array.isArray(rawTeams)
    ? rawTeams.map((t) => ({ id: t.id, name: t.name }))
    : [];
  const { sortByKey } = useTableFunctions();
  const isMatchTable = Boolean(gameIdsByMatchId);
  const isCompactTable = isMatchTable || Boolean(compactColumns);

  const [sortKey, setSortKey] = useState<keyof T | null>(null);
  const [asc, setAsc] = useState(false);
  const [statusFilter, setStatusFilter] = useState<MatchStatusFilter>("ALL");
  const [selectedIds, setSelectedIds] = useState<Array<string>>([]);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Array<string>>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const [addItemForm, setAddItemForm] = useState<Record<string, string>>({});
  const [editItemForm, setEditItemForm] = useState<Record<string, string>>({});
  const [activeEditingRecord, setActiveEditingRecord] = useState<T | null>(null);
  const [isPendingSave, setIsPendingSave] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const statusCounts = useMemo(() => {
    if (!isMatchTable) return null;

    const counts: Record<MatchStatusFilter, number> = {
      ALL: data.length,
      UPCOMING: 0,
      LIVE: 0,
      FINISHED: 0,
    };

    data.forEach((item) => {
      const status = normalizeMatchStatus((item as { status?: string }).status);
      counts[status] += 1;
    });

    return counts;
  }, [data, isMatchTable]);

  const statusFilteredData = useMemo(() => {
    if (!isMatchTable || statusFilter === "ALL") return data;

    return data.filter(
      (item) => normalizeMatchStatus((item as { status?: string }).status) === statusFilter,
    );
  }, [data, isMatchTable, statusFilter]);

  const supplementGapsByMatchId = useMemo(() => {
    if (!gamesByMatchId) return {} as Record<string, MatchSupplementGaps>;

    const map: Record<string, MatchSupplementGaps> = {};
    for (const [matchId, games] of Object.entries(gamesByMatchId)) {
      map[matchId] = getMatchSupplementGaps(games);
    }
    return map;
  }, [gamesByMatchId]);

  const displayData = useMemo(() => {
    if (!sortKey) return statusFilteredData;

    if (isMatchTable && String(sortKey) === "missingData") {
      return [...statusFilteredData].sort((a, b) => {
        const aId = String(a[idKey]);
        const bId = String(b[idKey]);
        const emptyGaps: MatchSupplementGaps = { dragons: false, bans: false, csAt15: false };
        const aMissing = matchHasSupplementGaps(supplementGapsByMatchId[aId] ?? emptyGaps) ? 1 : 0;
        const bMissing = matchHasSupplementGaps(supplementGapsByMatchId[bId] ?? emptyGaps) ? 1 : 0;
        return asc ? aMissing - bMissing : bMissing - aMissing;
      });
    }

    return sortByKey(statusFilteredData, sortKey, asc);
  }, [statusFilteredData, sortKey, asc, sortByKey, isMatchTable, idKey, supplementGapsByMatchId]);

  useEffect(() => {
    setSelectedIds([]);
  }, [statusFilter, sortKey, asc]);

  useEffect(() => {
    if (isAddModalOpen && !FormComponent) {
      const initialForm: Record<string, string> = {};
      keys.forEach((key) => {
        if (key !== idKey) initialForm[String(key)] = "";
      });
      setAddItemForm(initialForm);
    }
  }, [isAddModalOpen, keys, idKey, FormComponent]);

  const handleSort = (key: keyof T) => {
    if (sortKey === key) {
      setAsc((prev) => !prev);
      return;
    }
    setSortKey(key);
    setAsc(false);
  };

  const openMatchModal = () => {
    ensureGamesLoaded?.();
  };

  const handleSelectAllClick = (checked: boolean) => {
    if (checked) {
      setSelectedIds(displayData.map((item) => String(item[idKey])));
    } else {
      setSelectedIds([]);
    }
  };

  const handleRowSelectClick = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const openDeleteModal = (ids: Array<string>) => {
    setItemToDelete(ids);
    setDeleteError(null);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setIsDeleteModalOpen(false);
    setItemToDelete([]);
    setDeleteError(null);
  };

  const handleFinalDelete = async () => {
    if (!onDeleteConfirm) return;

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await Promise.resolve(onDeleteConfirm(itemToDelete));
      setSelectedIds((prev) => prev.filter((id) => !itemToDelete.includes(id)));
      setIsDeleteModalOpen(false);
      setItemToDelete([]);
      setDeleteError(null);
    } catch (err) {
      setDeleteError(getErrorMessage(err, "Poisto epäonnistui. Yritä uudelleen."));
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = (item: T) => {
    if (isMatchTable) {
      openMatchModal();
    }
    setActiveEditingRecord(item);
    setSubmitError(null);

    if (!FormComponent) {
      const stringifiedForm: Record<string, string> = {};

      keys.forEach((key) => {
        const val = (item as Record<string, unknown>)[String(key)];

        if (val === undefined || val === null) {
          stringifiedForm[String(key)] = "";
        } else if (typeof val === "string" || typeof val === "number" || typeof val === "boolean") {
          stringifiedForm[String(key)] = val.toString();
        } else {
          stringifiedForm[String(key)] = "";
        }
      });

      setEditItemForm(stringifiedForm);
    }

    setIsEditModalOpen(true);
  };

  const openAddModal = () => {
    setSubmitError(null);
    if (isMatchTable) {
      openMatchModal();
    }
    setIsAddModalOpen(true);
  };

  const closeAddModal = () => {
    if (isPendingSave) return;
    setIsAddModalOpen(false);
  };

  const closeEditModal = () => {
    if (isPendingSave) return;
    setIsEditModalOpen(false);
    setActiveEditingRecord(null);
  };

  const handleFallbackEditSubmit = async () => {
    if (!onEdit || !activeEditingRecord) return;

    const reconstructedItem = { ...activeEditingRecord } as Record<string, unknown>;
    keys.forEach((key) => {
      const keyStr = String(key);
      if (keyStr !== String(idKey)) {
        reconstructedItem[keyStr] = coerceValueType(
          keyStr,
          editItemForm[keyStr],
          activeEditingRecord,
        );
      }
    });

    setIsPendingSave(true);
    setSubmitError(null);
    try {
      await Promise.resolve(onEdit(reconstructedItem as T));
      setIsEditModalOpen(false);
      setActiveEditingRecord(null);
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Päivitys epäonnistui. Yritä uudelleen."));
    } finally {
      setIsPendingSave(false);
    }
  };

  const handleFallbackCreateSubmit = async () => {
    if (!onCreateSubmit) return;

    const castedPayload: Record<string, string | number | boolean> = {};
    const sampleItem = data[0];
    Object.keys(addItemForm).forEach((keyStr) => {
      castedPayload[keyStr] = coerceValueType(keyStr, addItemForm[keyStr], sampleItem);
    });

    setIsPendingSave(true);
    setSubmitError(null);
    try {
      await Promise.resolve(onCreateSubmit(castedPayload as unknown as Partial<T>));
      setIsAddModalOpen(false);
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Tallennus epäonnistui. Yritä uudelleen."));
    } finally {
      setIsPendingSave(false);
    }
  };

  const handleFormCreateSubmit = async (newData: Partial<T>) => {
    setIsPendingSave(true);
    setSubmitError(null);
    try {
      if (onCreateSubmit) {
        await Promise.resolve(onCreateSubmit(newData));
      }
      setIsAddModalOpen(false);
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Tallennus epäonnistui. Yritä uudelleen."));
      throw err;
    } finally {
      setIsPendingSave(false);
    }
  };

  const handleFormEditSubmit = async (updatedData: Partial<T>) => {
    if (!activeEditingRecord) return;
    setIsPendingSave(true);
    setSubmitError(null);
    try {
      if (onEdit) {
        await Promise.resolve(onEdit({ ...activeEditingRecord, ...updatedData } as T));
      }
      setIsEditModalOpen(false);
      setActiveEditingRecord(null);
    } catch (err) {
      setSubmitError(getErrorMessage(err, "Päivitys epäonnistui. Yritä uudelleen."));
      throw err;
    } finally {
      setIsPendingSave(false);
    }
  };

  const updateAddItemField = (key: string, value: string) => {
    setAddItemForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateEditItemField = (key: string, value: string) => {
    setEditItemForm((prev) => ({ ...prev, [key]: value }));
  };

  return {
    availableTeams,
    isMatchTable,
    isCompactTable,
    statusFilter,
    setStatusFilter,
    statusCounts,
    displayData,
    supplementGapsByMatchId,
    handleSort,
    selectedIds,
    handleSelectAllClick,
    handleRowSelectClick,
    openDeleteModal,
    closeDeleteModal,
    handleFinalDelete,
    isDeleteModalOpen,
    itemToDelete,
    deleteError,
    isDeleting,
    openAddModal,
    closeAddModal,
    isAddModalOpen,
    openEditModal,
    closeEditModal,
    isEditModalOpen,
    activeEditingRecord,
    addItemForm,
    editItemForm,
    updateAddItemField,
    updateEditItemField,
    isPendingSave,
    submitError,
    handleFallbackCreateSubmit,
    handleFallbackEditSubmit,
    handleFormCreateSubmit,
    handleFormEditSubmit,
  };
};
